import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { admin, api } from '../../api/client.js'
import { useApi } from '../../api/useApi.js'
import { btn, Field, inputClass, Notice, Section, useAction } from '../../components/admin/ui.jsx'
import { pickI18n } from '../../i18n'
import { money } from '../../lib/money.js'

const today = () => new Date().toISOString().slice(0, 10)

/** Expenses (what the fund paid for) and the donations log. */
export default function AdminFinancePage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const expenses = useApi(() => admin.expenses())
  const feeders = useApi(() => api.feeders())
  const summary = useApi(() => api.donationSummary())
  const { status, busy, run } = useAction()
  const empty = { spent_on: today(), category: 'food', amount: '', description: '', food_kg: '', feeder_id: '', receipt_url: '' }
  const [form, setForm] = useState(empty)
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
  const fmt = (c) => money(c, summary.data?.currency ?? 'eur', lang)

  const add = async (e) => {
    e.preventDefault()
    const body = {
      spent_on: `${form.spent_on}T12:00:00Z`,
      category: form.category,
      amount_cents: Math.round(Number(form.amount.replace(',', '.')) * 100),
      description: form.description.trim(),
      food_grams: form.food_kg ? Math.round(Number(form.food_kg.replace(',', '.')) * 1000) : null,
      feeder_id: form.feeder_id || null,
      receipt_url: form.receipt_url.trim() || null,
    }
    if (await run(() => admin.addExpense(body), t('admin.finance.saved'))) {
      setForm(empty)
      expenses.reload()
      summary.reload()
    }
  }
  const remove = async (id) => {
    if (!window.confirm(t('admin.finance.deleteConfirm'))) return
    await run(() => admin.deleteExpense(id))
    expenses.reload()
    summary.reload()
  }

  return (
    <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 pt-8 pb-16 sm:px-8">
      <Link to="/admin" className="text-sm font-bold text-subtle no-underline hover:text-ink">{t('admin.back')}</Link>
      <h1 className="text-[clamp(28px,3.5vw,40px)] font-black tracking-[-1px]">{t('admin.finance.title')}</h1>
      {summary.data ? (
        <p className="text-lg text-muted">
          {t('donate.raised')}: <b>{fmt(summary.data.raised_cents)}</b> · {t('donate.spent')}: <b>{fmt(summary.data.spent_cents)}</b> ·{' '}
          {t('donate.balance')}: <b>{fmt(summary.data.balance_cents)}</b>
          {!summary.data.enabled ? <span className="block text-sm">{t('admin.finance.stripeOff')}</span> : null}
        </p>
      ) : null}
      <Section title={t('admin.finance.addExpense')}>
        <form onSubmit={add} className="grid gap-3 md:grid-cols-3">
          <Field label={t('admin.finance.date')}>
            <input type="date" required value={form.spent_on} onChange={set('spent_on')} className={inputClass} />
          </Field>
          <Field label={t('admin.finance.category')}>
            <select value={form.category} onChange={set('category')} className={inputClass}>
              {['food', 'sim', 'hardware', 'other'].map((c) => (
                <option key={c} value={c}>{t(`donate.category.${c}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('admin.finance.amount')}>
            <input required inputMode="decimal" placeholder="45.00" value={form.amount} onChange={set('amount')} className={inputClass} />
          </Field>
          <Field label={t('admin.finance.description')} className="md:col-span-2">
            <input required minLength={2} maxLength={300} value={form.description} onChange={set('description')} className={inputClass} />
          </Field>
          <Field label={t('admin.finance.foodKg')}>
            <input inputMode="decimal" value={form.food_kg} onChange={set('food_kg')} className={inputClass} />
          </Field>
          <Field label={t('admin.finance.feeder')}>
            <select value={form.feeder_id} onChange={set('feeder_id')} className={inputClass}>
              <option value="">{t('donate.generalFund')}</option>
              {(feeders.data ?? []).map((f) => (
                <option key={f.id} value={f.id}>{pickI18n(f.name, lang)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('admin.finance.receipt')} className="md:col-span-2">
            <input type="url" placeholder="https://…" value={form.receipt_url} onChange={set('receipt_url')} className={inputClass} />
          </Field>
          <button type="submit" disabled={busy} className={`${btn.primary} md:col-start-3`}>{t('admin.finance.add')}</button>
        </form>
        <Notice status={status} />
      </Section>
      <Section title={t('donate.expenses')}>
        <ul className="m-0 flex list-none flex-col p-0">
          {(expenses.data ?? []).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 border-b border-line py-3">
              <span className="flex flex-col">
                <span className="font-extrabold">{e.description}</span>
                <span className="text-sm text-subtle">
                  {new Date(e.spent_on).toLocaleDateString(lang)} · {t(`donate.category.${e.category}`)}
                  {e.food_grams ? ` · ${(e.food_grams / 1000).toFixed(1)} kg` : ''}
                </span>
              </span>
              <span className="flex items-center gap-3">
                <b>{fmt(e.amount_cents)}</b>
                <button type="button" onClick={() => remove(e.id)} className={btn.danger}>✕</button>
              </span>
            </li>
          ))}
        </ul>
      </Section>
    </main>
  )
}
